import {
  HoldAuditTrailDto,
  HoldResponseDto,
  HoldRestrictionType,
  UpsertHoldDto,
} from '@planning/app/api'
import { holdService } from '@planning/services'
import { FieldStore, FormStore, ListFieldStore, required, validateSubForm } from '@tom-ui/utils'
import { action, computed, makeObservable, observable } from 'mobx'

let _id = 0
const nextId = () => ++_id

export type CustomsStatus = 'Pending' | 'UnderInspection' | 'Cleared' | 'Refused'

export type CustomsEntryItem = {
  id: number
  ref: FieldStore<string>
  status: FieldStore<CustomsStatus>
}

export const CUSTOMS_STATUS_OPTIONS: { value: CustomsStatus; label: string }[] = [
  { value: 'Pending', label: 'Pending' },
  { value: 'UnderInspection', label: 'Under inspection' },
  { value: 'Cleared', label: 'Cleared' },
  { value: 'Refused', label: 'Refused' },
]

const createCustomsEntry = (initial?: {
  ref?: string
  status?: CustomsStatus
}): CustomsEntryItem => ({
  id: nextId(),
  ref: new FieldStore<string>(initial?.ref ?? ''),
  status: new FieldStore<CustomsStatus>(initial?.status ?? 'Pending'),
})

export interface HoldEntryData {
  holdTypeId: number | null
  holdTypeName?: string
  description: string
  customsEntries: Array<{ ref: string; status: CustomsStatus }>
  backendId?: number
}

export class HoldEntryStore {
  readonly id = nextId()
  readonly backendId: number | undefined
  holdTypeId: FieldStore<number | null>
  holdTypeName: FieldStore<string>
  description: FieldStore<string>
  customsEntries: ListFieldStore<CustomsEntryItem>

  constructor(initialValues?: Partial<HoldEntryData>) {
    makeObservable(this, {
      addCustomsEntry: action,
      removeCustomsEntry: action,
    })

    this.backendId = initialValues?.backendId
    this.holdTypeId = new FieldStore<number | null>(initialValues?.holdTypeId ?? null, required())
    this.holdTypeName = new FieldStore<string>(initialValues?.holdTypeName ?? '')
    this.description = new FieldStore<string>(initialValues?.description ?? '')
    this.customsEntries = new ListFieldStore<CustomsEntryItem>(
      (initialValues?.customsEntries ?? []).map(e => createCustomsEntry(e)),
    )
  }

  addCustomsEntry = () => {
    this.customsEntries.push(createCustomsEntry())
  }

  removeCustomsEntry = (index: number) => {
    this.customsEntries.removeByIndex(index)
  }

  get data(): HoldEntryData {
    return {
      holdTypeId: this.holdTypeId.value,
      holdTypeName: this.holdTypeName.value,
      description: this.description.value,
      customsEntries: this.customsEntries.value.map(e => ({
        ref: e.ref.value,
        status: e.status.value,
      })),
    }
  }
}

export interface OrderHoldsSectionData {
  holds: HoldEntryData[]
}

export class OrderHoldsSectionStore extends FormStore<OrderHoldsSectionData> {
  holds: ListFieldStore<HoldEntryStore>
  holdsAuditTrail: HoldAuditTrailDto[] = []
  isAuditTrailFetched = false
  isOverrideDialogOpen = false
  overrideHoldId: number | null = null
  resolveHoldId: number | null = null
  private currentJourneyId: number | null = null

  constructor(initialValues?: Partial<OrderHoldsSectionData>) {
    super(initialValues)

    makeObservable(this, {
      addHold: action,
      removeHold: action,
      loadFromDtos: action,
      holdsAuditTrail: observable,
      isAuditTrailFetched: observable,
      isOverrideDialogOpen: observable,
      overrideHoldId: observable,
      openOverrideDialog: action,
      closeOverrideDialog: action,

      newHolds: computed,
    })

    this.holds = new ListFieldStore<HoldEntryStore>(
      (initialValues?.holds ?? []).map(h => new HoldEntryStore(h)),
    )
  }

  addHold = () => {
    this.holds.push(new HoldEntryStore())
  }

  removeHold = (index: number) => {
    this.holds.removeByIndex(index)
  }

  loadFromDtos = (holds: HoldResponseDto[]) => {
    this.holds.setValue(
      holds.map(
        h =>
          new HoldEntryStore({
            backendId: h.id,
            holdTypeId: h.holdType?.id ?? null,
            holdTypeName: h.holdType?.name ?? '',
            description: h.reason ?? '',
            customsEntries: [],
          }),
      ),
    )
  }

  fetchHoldsAuditTrail = async (journeyId: number) => {
    this.currentJourneyId = journeyId
    const data = await holdService.fetchHoldsAuditTrail(journeyId)
    this.holdsAuditTrail = data
    this.isAuditTrailFetched = true
  }

  openOverrideDialog = (holdId: number) => {
    this.overrideHoldId = holdId
    this.isOverrideDialogOpen = true
  }

  closeOverrideDialog = () => {
    this.overrideHoldId = null
    this.isOverrideDialogOpen = false
  }

  override = async (holdId: number, operationTypes: HoldRestrictionType[], reason: string) => {
    await holdService.override({ holdIds: [holdId], operationTypes, reason })
    this.closeOverrideDialog()
    if (this.currentJourneyId !== null) {
      await this.fetchHoldsAuditTrail(this.currentJourneyId)
    }
  }

  get newHolds() {
    return this.holds.value.filter(h => h.backendId === undefined && h.holdTypeId.value !== null)
  }

  validate = () => {
    return validateSubForm(this)
  }

  get upsertPayload(): UpsertHoldDto[] {
    return this.holds.value
      .filter(h => h.holdTypeId.value !== null)
      .map(h => ({
        id: h.backendId,
        holdTypeId: h.holdTypeId.value!,
        reason: h.description.value,
        contact: h.description.value,
      }))
  }

  get data(): OrderHoldsSectionData {
    return {
      holds: this.holds.value.map(h => h.data),
    }
  }
}
